<template>
  <div class="font-sans flex flex-col min-h-screen bg-gradient-to-br from-blue-600 via-indigo-500 to-purple-700 relative">
    <!-- Animated Background Elements -->
    <div class="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
      <div class="absolute w-96 h-96 bg-white opacity-10 rounded-full -top-20 -left-20 animate-pulse-slow"></div>
      <div class="absolute w-80 h-80 bg-blue-300 opacity-10 rounded-full top-40 right-10 animate-float"></div>
      <div class="absolute w-64 h-64 bg-purple-300 opacity-10 rounded-full bottom-20 left-40 animate-float-delayed"></div>
    </div>
    
    <header class="py-5 px-5 flex justify-between items-center relative z-10">
      <div class="logo-text-pages flex items-center">
        <span class="text-white">inter<span class="text-yellow-300">bot</span></span>
        <div class="ml-2 bg-yellow-300 p-1 rounded-md">
          <span class="text-sm font-bold text-blue-700">IA</span>
        </div>
      </div>
      <div class="flex flex-col justify-between w-6 h-5 cursor-pointer">
        <span class="h-0.5 bg-white"></span>
        <span class="h-0.5 bg-white"></span>
        <span class="h-0.5 bg-white"></span>
      </div>
    </header>

    <main class="flex flex-col items-center px-5 flex-grow relative z-10">
      <!-- Hero Section -->
      <div class="text-center mb-5 max-w-4xl mt-8">
        <h1 class="text-5xl md:text-6xl font-bold leading-tight mb-6 text-white drop-shadow-lg">
          Domina Tus Entrevistas con IA
        </h1>
        <p class="text-white text-xl opacity-90 max-w-2xl mx-auto mb-8">
          Practica, mejora y destaca en tu próxima oportunidad laboral con nuestra entrevistadora virtual impulsada por inteligencia artificial
        </p>
      </div>
      
      <!-- Video Section with Highlight Effect -->
      <div class="w-full max-w-5xl mx-auto relative">
        <div class="absolute -inset-0.5 bg-gradient-to-r from-yellow-300 to-pink-500 rounded-xl blur opacity-70 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse-slow"></div>
        <div class="relative w-full rounded-xl overflow-hidden shadow-2xl" @click="playVideo">
          <!-- Mobile Video (Hidden on larger screens) -->
          <div v-if="!videoPlaying" class="block md:hidden">
            <img src="../assets/video-placeholder.png" alt="Video Thumbnail" class="w-full block transform transition duration-500 hover:scale-105"/>
          </div>
          <video v-if="videoPlaying && !isLargeScreen" ref="videoPlayer" :src="mobileVideoSource" controls class="block md:hidden w-full"></video>

          <!-- Wide Screen Video (Hidden on smaller screens) -->
          <div v-if="!videoPlaying" class="hidden md:block">
            <img src="../assets/video-placeholder.png" alt="Video Thumbnail" class="w-full block transform transition duration-500 hover:scale-105"/>
          </div>
          <video v-if="videoPlaying && isLargeScreen" ref="videoPlayer" :src="wideScreenVideoSource" controls class="hidden md:block w-full"></video>

          <button v-if="!videoPlaying" class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-full flex justify-center items-center cursor-pointer transition-all hover:scale-110 hover:bg-opacity-30 border-2 border-white border-opacity-40">
            <div class="w-0 h-0 border-t-[15px] border-b-[15px] border-l-[25px] border-transparent border-l-white ml-2"></div>
          </button>
          <div v-if="!videoPlaying" class="absolute bottom-2 left-2 w-[calc(100%-16px)] flex items-center justify-start text-white">
            <span class="mr-1 text-base">0:05</span>
            <div class="flex-1 h-[5px] bg-white bg-opacity-30 rounded-md mr-1 overflow-hidden">
              <div class="h-full w-1/4 bg-yellow-300 rounded-md"></div>
            </div>
            <div class="flex items-center gap-1">
              <span class="cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" class="bi bi-gear-fill" viewBox="0 0 16 16">
                  <path d="M9.405 1.05c-.413-.382-1.07-.382-1.483 0l-6 5.5a.5.5 0 0 0 0 .816l6 5.5c.413.382 1.07.382 1.483 0l6-5.5a.5.5 0 0 0 0-.816l-6-5.5z"/>
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA Button with Animation -->
      <router-link to="/terms" class="mt-10 bg-gradient-to-r from-yellow-400 to-yellow-500 text-blue-900 font-bold py-4 px-8 rounded-full text-lg cursor-pointer inline-block transition-all duration-300 hover:shadow-lg hover:scale-105 transform hover:-translate-y-1 shadow-md">
        Inicia Tu Entrevista Ahora
        <span class="ml-2">→</span>
      </router-link>

      <!-- Value Proposition -->
      <div class="text-center mt-16 max-w-4xl mx-auto bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-2xl shadow-xl">
        <h2 class="text-3xl font-bold text-white mb-6 text-center">
          Practica para tus entrevistas con nuestra IA
        </h2>
        <p class="text-lg text-white text-opacity-90 leading-relaxed mb-5">
          ¿Buscas un trabajo que te apasione? La preparación es clave. Nuestra IA te ofrece la forma más efectiva y moderna de practicar para tus entrevistas, con análisis detallado y feedback preciso.
        </p>
        <p class="text-white font-bold text-xl animate-pulse">Completamente gratuito durante nuestra fase beta</p>
      </div>
      
    </main>

    <!-- Report Section -->
    <section class="text-center mt-20 relative">
      <div class="container mx-auto px-4 text-center">
        <div class="absolute -inset-1 bg-gradient-to-r from-blue-300 to-purple-300 rounded-3xl blur opacity-30 group-hover:opacity-100 transition duration-500 group-hover:duration-200"></div>
        <div class="relative bg-white bg-opacity-10 backdrop-blur-sm rounded-3xl p-8 shadow-xl">
          <h2 class="text-3xl font-bold text-white mb-8 text-center">
            Obtén un Reporte Detallado
          </h2>
          <div class="flex justify-center mb-6 transform hover:scale-105 transition-transform duration-500">
            <img
              src="../assets/reporte.png"
              alt="Reporte de Entrevista"
              class="rounded-lg shadow-xl max-w-full h-auto border-4 border-white border-opacity-20"
              style="max-height: 500px"
            />
          </div>
          <p class="text-white text-opacity-80 text-center text-lg">Tu análisis personalizado después de cada entrevista</p>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-20">
      <div class="container mx-auto px-4">
        <h2 class="text-3xl font-bold text-white mb-10 text-center relative">
          <span class="bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 to-yellow-400">Trabaja en los aspectos clave</span>
          <div class="absolute h-1 w-20 bg-yellow-300 bottom-0 left-1/2 transform -translate-x-1/2 rounded-full"></div>
        </h2>
        <p class="text-center text-white text-opacity-90 text-lg mb-8 max-w-3xl mx-auto">
          Nuestro modelo de análisis avanzado identifica aspectos clave para mejorar tu desempeño y aumentar tus probabilidades de obtener la posición ideal.
        </p>
        <p class="text-center text-white text-opacity-90 text-lg mb-12 max-w-3xl mx-auto">
          <span class="font-bold text-yellow-300">Promoción por tiempo limitado:</span> Reporte completamente gratuito y anónimo. Sin datos de pago ni información personal.
        </p>
        
        <div class="flex flex-wrap justify-center gap-8">
          <!-- Card 1 -->
          <div class="w-full sm:w-1/2 md:w-1/3 max-w-sm">
            <div class="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden flex flex-col h-full transform hover:-translate-y-2 transition-all duration-300 border border-white border-opacity-20">
              <div class="relative">
                <img src="../assets/image-11.jpg" alt="Mujer Joven Pensando" class="w-full h-48 object-cover filter brightness-90" />
                <div class="absolute inset-0 bg-gradient-to-t from-blue-900 to-transparent opacity-60"></div>
              </div>
              <div class="p-8">
                <div class="w-14 h-14 bg-yellow-400 rounded-full flex items-center justify-center mb-4 shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-blue-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 class="text-xl font-bold text-white mb-4">
                  Personalidad<br><span class="text-yellow-300">Sé tú mismo, siempre</span>
                </h3>
                <p class="text-white text-opacity-80 mb-6">
                  El mejor trabajo es el que se alinea con tus valores y aspiraciones. Te ayudamos a identificar tus rasgos y fortalezas para encontrar tu match perfecto.
                </p>
                <button class="bg-blue-800 bg-opacity-50 hover:bg-opacity-70 border border-blue-400 text-white rounded-lg py-3 px-6 transition-colors w-full font-medium">
                  ¿La posición es para mí?
                </button>
              </div>
            </div>
          </div>

          <!-- Card 2 -->
          <div class="w-full sm:w-1/2 md:w-1/3 max-w-sm">
            <div class="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden flex flex-col h-full transform hover:-translate-y-2 transition-all duration-300 border border-white border-opacity-20">
              <div class="relative">
                <img src="../assets/image-14.jpg" alt="Hombre pensando" class="w-full h-48 object-cover filter brightness-90" />
                <div class="absolute inset-0 bg-gradient-to-t from-blue-900 to-transparent opacity-60"></div>
              </div>
              <div class="p-8">
                <div class="w-14 h-14 bg-yellow-400 rounded-full flex items-center justify-center mb-4 shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-blue-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 class="text-xl font-bold text-white mb-4">
                  Desarrollo<br><span class="text-yellow-300">Identifica oportunidades</span>
                </h3>
                <p class="text-white text-opacity-80 mb-6">
                  Te ayudamos a identificar qué áreas específicas necesitas mejorar para destacar en tus próximas entrevistas y acelerar tu carrera.
                </p>
                <button class="bg-blue-800 bg-opacity-50 hover:bg-opacity-70 border border-blue-400 text-white rounded-lg py-3 px-6 transition-colors w-full font-medium">
                  ¿Tengo las habilidades necesarias?
                </button>
              </div>
            </div>
          </div>

          <!-- Card 3 -->
          <div class="w-full sm:w-1/2 md:w-1/3 max-w-sm">
            <div class="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden flex flex-col h-full transform hover:-translate-y-2 transition-all duration-300 border border-white border-opacity-20">
              <div class="relative">
                <img src="../assets/image-13.jpg" alt="Joven con lentes" class="w-full h-48 object-cover filter brightness-90" />
                <div class="absolute inset-0 bg-gradient-to-t from-blue-900 to-transparent opacity-60"></div>
              </div>
              <div class="p-8">
                <div class="w-14 h-14 bg-yellow-400 rounded-full flex items-center justify-center mb-4 shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-blue-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 class="text-xl font-bold text-white mb-4">
                  Desempeño<br><span class="text-yellow-300">Análisis completo</span>
                </h3>
                <p class="text-white text-opacity-80 mb-6">
                  Recibe un reporte detallado de tu entrevista con consejos específicos para mejorar tu presentación y respuestas en situaciones reales.
                </p>
                <button class="bg-blue-800 bg-opacity-50 hover:bg-opacity-70 border border-blue-400 text-white rounded-lg py-3 px-6 transition-colors w-full font-medium">
                  ¿Cómo puedo destacar?
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

  <!-- FAQ Section -->
  <section class="py-20">
    <div class="container mx-auto px-4 max-w-4xl">
      <h2 class="text-3xl font-bold text-white mb-10 text-center relative">
        <span class="bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 to-yellow-400">Preguntas Frecuentes</span>
        <div class="absolute h-1 w-20 bg-yellow-300 bottom-0 left-1/2 transform -translate-x-1/2 rounded-full"></div>
      </h2>
      <div class="space-y-4">
        <div v-for="(item, index) in faqItems" :key="index" 
             class="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl overflow-hidden border border-white border-opacity-20 transition-all duration-300 hover:bg-opacity-15">
          <button
            class="flex items-center justify-between w-full p-5 text-white focus:outline-none"
            @click="toggleAccordion(index)"
          >
            <span class="font-bold text-lg">{{ item.question }}</span>
            <span :class="{ 'rotate-180': activeAccordion === index }" class="transition-transform duration-300 bg-blue-500 rounded-full p-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </button>
          <div
            v-if="activeAccordion === index"
            class="p-5 border-t border-white border-opacity-20"
          >
           <p class="text-white text-opacity-90">
             {{ item.answer }}
           </p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Footer -->
  <footer class="bg-gradient-to-r from-blue-900 to-indigo-900 py-10 text-white border-t border-white border-opacity-20">
    <div class="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center">
      <div class="mb-6 md:mb-0 text-center md:text-left">
        <div class="logo-text-pages flex items-center justify-center md:justify-start mb-3">
          <span class="text-white text-3xl">inter<span class="text-yellow-300">bot</span></span>
          <div class="ml-2 bg-yellow-300 p-1 rounded-md">
            <span class="text-xs font-bold text-blue-700">IA</span>
          </div>
        </div>
        <p class="text-sm text-white text-opacity-80">
          © {{ new Date().getFullYear() }} Interbot. Todos los derechos reservados.
        </p>
      </div>
      <nav class="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-8 text-center md:text-left">
        <button @click="showPrivacyModal = true" class="text-white hover:text-yellow-300 transition-colors font-medium">
          Aviso de Privacidad
        </button>
        <button @click="showTermsModal = true" class="text-white hover:text-yellow-300 transition-colors font-medium">
          Términos de Uso
        </button>
        <button @click="openModal()" class="bg-gradient-to-r from-yellow-400 to-yellow-500 text-blue-900 font-bold py-2 px-6 rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 transform">
          Contacto
        </button>
      </nav>
      <div class="mt-6 md:mt-0 text-center md:text-right">
        <p class="text-sm text-white text-opacity-80">
          Powered By JRUG
        </p>
        <div class="flex justify-center md:justify-end space-x-4 mt-3">
          <a href="#" class="text-white hover:text-yellow-300 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.167 6.839 9.49.5.09.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.022A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.291 2.747-1.022 2.747-1.022.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.933.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z"/>
            </svg>
          </a>
          <a href="#" class="text-white hover:text-yellow-300 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M22.162 5.656a8.384 8.384 0 01-2.402.658A4.196 4.196 0 0021.6 4c-.82.488-1.719.83-2.656 1.015a4.182 4.182 0 00-7.126 3.814 11.874 11.874 0 01-8.62-4.37 4.168 4.168 0 00-.566 2.103c0 1.45.738 2.731 1.86 3.481a4.168 4.168 0 01-1.894-.523v.052a4.185 4.185 0 003.355 4.101 4.21 4.21 0 01-1.89.072A4.185 4.185 0 007.97 16.65a8.394 8.394 0 01-6.191 1.732 11.83 11.83 0 006.41 1.88c7.693 0 11.9-6.373 11.9-11.9 0-.18-.005-.362-.013-.54a8.496 8.496 0 002.087-2.165z"/>
            </svg>
          </a>
          <a href="#" class="text-white hover:text-yellow-300 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M16 8.245V6.5a1.5 1.5 0 00-1.5-1.5h-9A1.5 1.5 0 004 6.5v9A1.5 1.5 0 005.5 17h9a1.5 1.5 0 001.5-1.5v-1.745l4 3.245V5l-4 3.245z"/>
            </svg>
          </a>
        </div>
      </div>
    </div>
  </footer>

  <!-- Privacy Modal -->
  <div v-if="showPrivacyModal" class="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
            <div class="bg-white p-8 rounded-lg shadow-xl max-w-2xl w-full">
                 <h2 class="text-2xl font-semibold text-gray-800 mb-4">Aviso de Privacidad</h2>
                <div class="max-h-96 overflow-y-auto mb-4">
                  <p class="text-gray-700">
                    Su privacidad es importante para nosotros. Interbot está diseñado para operar sin la recolección de ningún dato personal. Como resultado, le pedimos que no proporcione información personal o confidencial, como nombres, direcciones, números de teléfono o cualquier otro dato que pueda ser utilizado para identificarle. Por favor, mantenga su información personal fuera de nuestros sistemas. Nuestro objetivo es proporcionarle una experiencia segura y anónima.
                   </p>

                </div>
                <button @click="showPrivacyModal = false" class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline">Cerrar</button>
            </div>
        </div>

        <!-- Terms Modal -->
        <div v-if="showTermsModal" class="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
            <div class="bg-white p-8 rounded-lg shadow-xl max-w-2xl w-full">
                 <h2 class="text-2xl font-semibold text-gray-800 mb-4">Términos de Uso</h2>
                 <div class="max-h-96 overflow-y-auto mb-4">
                  <p class="text-gray-700">
                    Al acceder y utilizar Interbot, usted acepta los siguientes términos:
<br>
<br>1. Responsabilidad del Usuario:
<br>
<br>Usted es el único responsable del uso que haga de Interbot. Esto incluye cualquier acción realizada, decisión tomada o información obtenida al utilizar nuestros servicios.
<br>
<br>2. Naturaleza de la Información Proporcionada:
<br>
<br>La información proporcionada por Interbot es generada por un modelo de inteligencia artificial que se encuentra en una etapa de aprendizaje. Esta información no constituye un consejo profesional, y no debe ser tomada como tal.
<br>
<br>3. Recomendación de Asesoramiento Profesional:
<br>
<br>Le recomendamos que consulte a un experto cualificado para obtener consejos específicos y personalizados sobre cualquier asunto relevante a sus necesidades. La información generada por nuestra plataforma es solo una herramienta complementaria.
<br>
<br>4. Prohibición de Ingeniería Inversa y Acceso No Autorizado:
<br>
<br>Queda estrictamente prohibido cualquier forma de ingeniería inversa, intento de acceso no autorizado a la plataforma, o cualquier acción que busque acceder, extraer o copiar datos de la plataforma o del modelo de inteligencia artificial. El incumplimiento de esta norma puede resultar en acciones legales.
<br>
<br>5. Derechos de Autor y Propiedad Intelectual:
<br>
<br>Todo el contenido de este sitio web, incluyendo textos, imágenes, diseño, y cualquier otro material, está protegido por los derechos de autor de sus respectivos propietarios. Queda prohibida la reproducción, distribución o modificación no autorizada de este contenido.
<br>
<br>6. Aceptación de los Términos:
<br>
<br>Al utilizar Interbot, usted reconoce que ha leído, comprendido y aceptado estos términos de uso en su totalidad.
<br>    
</p>
                
           </div>
                <button @click="showTermsModal = false" class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline">Cerrar</button>
            </div>
        </div>

        <div v-if="showModal" class="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
    <div class="bg-white p-8 rounded-lg shadow-xl max-w-md w-full relative">
         <button @click="closeModal" class="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-200 focus:outline-none focus:bg-gray-300">
             <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
           </svg>
         </button>
      <h2 class="text-2xl font-semibold text-gray-800 mb-4">Contáctanos</h2>
      <form @submit.prevent="submitForm">
        <div class="mb-4">
          <label for="name" class="block text-gray-700 text-sm font-bold mb-2">Nombre</label>
          <input type="text" id="name" v-model="formData.name"
            class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            required>
        </div>
        <div class="mb-4">
          <label for="email" class="block text-gray-700 text-sm font-bold mb-2">Correo Electrónico</label>
          <input type="email" id="email" v-model="formData.email"
            class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            required>
        </div>
        <div class="mb-4">
          <label for="message" class="block text-gray-700 text-sm font-bold mb-2">Mensaje</label>
          <textarea id="message" v-model="formData.message" rows="4"
            class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            required></textarea>
        </div>
        <div class="flex justify-end">
          <button type="submit" class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline" :disabled="loading"
          :class="{'opacity-50 cursor-not-allowed': loading}"
          >Enviar</button>
        </div>
          <div v-if="submissionMessage" class="text-green-500 mt-4 text-center">
           {{ submissionMessage }}
            </div>
         <div v-if="errorMessage" class="text-red-500 mt-4 text-center">
            {{ errorMessage }}
         </div>
      </form>
    </div>
  </div>
    
  </div>
</template>

<script setup>
import axios from 'axios';
import { ref, onMounted, onUnmounted, nextTick} from 'vue';
import mobileVideo from '../assets/mobile_screen.mp4';
import wideVideo from '../assets/wide_screen.mp4';


const videoPlaying = ref(false);
const videoPlayer = ref(null);

const mobileVideoSource = ref(mobileVideo);
const wideScreenVideoSource = ref(wideVideo);

const isLargeScreen = ref(false);

const updateScreenSize = () => {
  isLargeScreen.value = window.innerWidth >= 768;
};

const playVideo = async () => {
    videoPlaying.value = true;

    await nextTick();
    if (videoPlayer.value) {
        videoPlayer.value.play();
    }
};

onMounted(() => {
    updateScreenSize();
  window.addEventListener('resize', updateScreenSize);
});

onUnmounted(() => {
    window.removeEventListener('resize', updateScreenSize);
});

</script>

<script>
export default {
  data() {
    return {
      showModal: false,
      formData: {
        name: '',
        email: '',
        message: '',
      },
      loading: false,
      submissionMessage: null,
        errorMessage: null,
        submitted: false, 
      showPrivacyModal: false,
      showTermsModal: false,
      activeAccordion: null,
      faqItems: [
        {
          question: "¿Cuánto tiempo toma hacer una entrevista aquí?",
          answer:
            "Entre 15 y 30 minutos. Te recomendamos hacerla en un espacio tranquilo y cómodo. Se puede realizar en un móvil, pero te recomendamos una computadora.",
        },
        {
          question: "¿Me van a cobrar algo o intentar vender algo?",
          answer: "No, por el momento este es un servicio gratuito ya que nos encontramos en etapa de pruebas. Cuando se lance la versión final, se cobrará una tarifa por el servicio y seremos muy claros y transparentes en eso.",
        },
        {
          question: "¿Usan está información para algo o la comparten con terceros?",
          answer:
            "Aunque tenemos medidas altas de seguridad implementadas, te pedimos que por el momento no nos compartas ninguna información personal ni confidencial. Actualmente la información de las entrevistas puede ser usada para entrenar y perfeccionar tanto nuestro modelo como el de nuestro proveedor de LLM (IA). No compartimos información con terceros salvo el servicio que nos provee el modelo de IA y el alojamiento en la nube de la plataforma.",
        },
         {
          question: "¿Puedo hacer más de una práctica de entrevista?",
          answer:
            "Si, actualmente el servicio no tiene limitación en la cantidad de entrevistas que una persona pueda realizar. Sin embargo, te pedimos que no abuses del servicio para que otros puedan tener la oportunidad de practicar.",
        },
         {
          question: "¿Quiénes son ustedes? ¿Por qué hacen esto?",
          answer:
            "Somos emprendedores mexicanos que queremos cambiar la forma en que se hace el reclutamiento en el mundo. Queremos que las personas encuentren trabajos que les apasionen y que las empresas encuentren a las personas que realmente encajen con su cultura y valores. Creemos que la tecnología puede ayudar a hacer esto de una forma más eficiente y justa. Este es nuestro primer paso en esa dirección.",
        },
      ],
    };
  },
  methods: {
    toggleAccordion(index) {
      this.activeAccordion = this.activeAccordion === index ? null : index;
    },
    openModal() {
      this.showModal = true;
      this.submitted = false; 
      this.loading = false;
    },
    closeModal() {
      this.showModal = false;
      this.resetForm();
      this.errorMessage = null;
        this.submissionMessage = null;
        this.submitted = false;
    },
    resetForm() {
      this.formData = {
          name: '',
          email: '',
          message: '',
       };
    },
    async submitForm() {
         this.loading = true;
          try {
            const response = await axios.post('/api/submit-contact', this.formData);
            if (response.status === 201) {
                this.submissionMessage = 'Form submitted successfully!';
                   this.errorMessage = null;
              this.submitted = true;  // <-- Set submitted to true after successful submission
            } else {
              this.errorMessage = 'Failed to submit form';
                  this.submissionMessage = null;
                   this.submitted = true; // <-- Set submitted to true after failed submission
           }
          } catch (error) {
            console.error('Error submitting form:', error);
            this.errorMessage = 'There was a problem submitting the form. Please try again.';
              this.submissionMessage = null;
             this.submitted = true;  // <-- Set submitted to true after error

          }
    },
  },
};
</script>

<style scoped>
@keyframes pulse-slow {
  0%, 100% {
    opacity: 0.7;
  }
  50% {
    opacity: 0.3;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

@keyframes float-delayed {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-15px);
  }
}

.animate-pulse-slow {
  animation: pulse-slow 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.animate-float {
  animation: float 8s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float-delayed 7s ease-in-out infinite;
  animation-delay: 2s;
}

/* Apply blur effect for glass morphism */
.backdrop-blur-sm {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* Highlight text with gradient */
.bg-clip-text {
  background-clip: text;
  -webkit-background-clip: text;
}

/* Enhanced button hover effects */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Improved card hover animations */
.hover\:scale-105:hover {
  transform: scale(1.05);
}

.hover\:-translate-y-1:hover {
  transform: translateY(-4px);
}

/* Style the modals with glass morphism */
.modal-glass {
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
</style>